import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HelpCircle, Plus, ShieldAlert, ShieldCheck, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import {
  getMinimumWages,
  createMinimumWage,
  deleteMinimumWage,
  getMinimumWageCompliance,
  getExpiringCertifications
} from '../../services/api/implementation/impl';
import {
  Tile
} from '../../../shared/components/ui/primitives';

/**
 * Notified minimum wages, and who is paid below them.
 *
 * The registers this system prints are handed to a labour inspector. Without
 * this screen, a rate below the statutory minimum produced a clean, dated
 * document proving underpayment and nobody was told. This is the warning that
 * should arrive before the inspector does.
 *
 * Three outcomes, not two. UNKNOWN — no minimum configured for that skill and
 * trade — is shown in its own column and its own colour, because "we were never
 * told the minimum" and "this rate clears it" are different facts and a green
 * tick for both would be a lie to whoever signs the register.
 */

const SKILLS = ['SKILLED', 'SEMI_SKILLED', 'UNSKILLED'];

const WageCompliance = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=labour' });
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [onlyBelow, setOnlyBelow] = useState(false);

  const rates = useQuery({ queryKey: ['minimumWages'], queryFn: () => getMinimumWages() });
  const compliance = useQuery({
    queryKey: ['minimumWageCompliance', onlyBelow],
    queryFn: () => getMinimumWageCompliance(onlyBelow ? { only_below: 'true' } : {})
  });
  const expiring = useQuery({
    queryKey: ['expiringCerts'],
    queryFn: () => getExpiringCertifications({ within_days: 30 })
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['minimumWages'] });
    queryClient.invalidateQueries({ queryKey: ['minimumWageCompliance'] });
  };

  const save = useMutation({
    mutationFn: createMinimumWage,
    onSuccess: () => {
      refresh();
      setAddOpen(false);
      toast.success('Minimum wage recorded');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not record the minimum wage')
  });

  const remove = useMutation({
    mutationFn: deleteMinimumWage,
    onSuccess: () => {
      refresh();
      toast.success('Removed');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not remove it')
  });

  const rateRows = rates.data?.data ?? [];
  const meta = compliance.data?.meta ?? {};
  const rows = compliance.data?.data?.rows ?? [];

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Minimum Wage Compliance' }} />

      <div className="space-y-4 p-4">
        {rateRows.length === 0 && (
          <div className="flex items-start gap-2 rounded-control border border-warning-border bg-warning-bg px-3 py-2 text-xs text-warning-fg">
            <ShieldAlert size={14} className="mt-0.5 shrink-0" />
            <span>
              No minimum wages have been recorded, so <strong>nothing is being
              checked</strong>. Minimum wages are notified per state and skill
              category and revised twice a year — none are pre-filled, because a
              wrong figure carrying the authority of a compliance check is worse
              than no check.
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Tile variant="card"
            label="Paid below minimum"
            value={meta.below ?? 0}
            tone={meta.below ? 'danger' : undefined}
            icon={<ShieldAlert size={15} />}
          />
          <Tile variant="card" label="Clear the minimum" value={meta.compliant ?? 0} icon={<ShieldCheck size={15} />} />
          <Tile variant="card"
            label="Not checked"
            value={meta.unknown ?? 0}
            tone={meta.unknown ? 'warning' : undefined}
            icon={<HelpCircle size={15} />}
          />
          <Tile variant="card" label="Daily shortfall" value={money(meta.total_daily_shortfall)}
            icon={<ShieldAlert size={18} />}
          />
        </div>

        {/* --- the notified figures --- */}
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <span className="font-medium text-gray-800">Notified minimum wages</span>
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-1.5 text-xs font-medium text-white"
            >
              <Plus size={13} /> Add
            </button>
          </div>
          {rates.isFetching ? (
            <Loader />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <Th>Applies to</Th>
                    <Th>State / zone</Th>
                    <Th className="text-right">Minimum / day</Th>
                    <Th>Effective from</Th>
                    <Th>Notification</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {rateRows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-gray-500">
                        Nothing recorded yet.
                      </td>
                    </tr>
                  )}
                  {rateRows.map((r) => (
                    <tr key={r.id} className="border-t border-gray-100">
                      <Td>
                        {[titleCase(r.skill_level), titleCase(r.trade)]
                          .filter(Boolean)
                          .join(' · ') || 'All workmen'}
                      </Td>
                      <Td className="text-gray-600">
                        {[r.state, r.zone].filter(Boolean).join(' · ') || '—'}
                      </Td>
                      <Td className="text-right font-medium tabular-nums">
                        {money(r.minimum_per_day)}
                      </Td>
                      <Td className="whitespace-nowrap">{shortDate(r.effective_from)}</Td>
                      <Td className="text-xs text-gray-500">
                        {r.notification_reference ?? '—'}
                      </Td>
                      <Td>
                        <button
                          type="button"
                          disabled={remove.isPending}
                          onClick={() => {
                            // eslint-disable-next-line no-alert
                            if (
                              window.confirm(
                                'Removing this stops it being checked against. To supersede a figure, add a new one with a later effective date instead.'
                              )
                            ) {
                              remove.mutate(r.id);
                            }
                          }}
                          className="rounded border border-danger-border px-2 py-1 text-xs text-danger-fg"
                          aria-label="Remove"
                        >
                          <Trash2 size={12} />
                        </button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* --- who is below --- */}
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <span className="font-medium text-gray-800">Every workman, checked</span>
            <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={onlyBelow}
                onChange={(e) => setOnlyBelow(e.target.checked)}
                className="h-3.5 w-3.5 accent-brand-700"
              />
              Only those below
            </label>
          </div>
          {compliance.isFetching ? (
            <Loader />
          ) : (
            <div className="max-h-[560px] overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 text-gray-600">
                  <tr>
                    <Th>Workman</Th>
                    <Th className="text-right">Rate / day</Th>
                    <Th className="text-right">Minimum</Th>
                    <Th className="text-right">Short by</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-10 text-center text-gray-500">
                        {onlyBelow
                          ? 'Nobody is paid below a configured minimum.'
                          : 'No active labour to check.'}
                      </td>
                    </tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.worker_id} className="border-t border-gray-100">
                      <Td>
                        <div className="font-medium text-gray-900">{r.name}</div>
                        <div className="text-xs text-gray-500">
                          {[titleCase(r.skill_level), r.trade, r.contractor ?? 'In-house']
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </Td>
                      <Td className="text-right tabular-nums">{money(r.rate_per_day)}</Td>
                      <Td className="text-right tabular-nums text-gray-500">
                        {r.minimum_per_day == null ? '—' : money(r.minimum_per_day)}
                      </Td>
                      <Td className="text-right font-medium tabular-nums text-danger-fg">
                        {r.shortfall ? money(r.shortfall) : ''}
                      </Td>
                      <Td><Verdict compliant={r.compliant} /></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-500">
          Rates below the minimum are flagged, never blocked. Stopping the muster
          would stop the day being recorded at all, and an unrecorded day is
          worse for the workman than an underpaid one.
        </p>

        <ExpiringCerts rows={expiring.data?.data ?? []} loading={expiring.isFetching} />
      </div>

      {addOpen && (
        <AddMinimumWageModal
          busy={save.isPending}
          onClose={() => setAddOpen(false)}
          onSubmit={(values) => save.mutate(values)}
        />
      )}
    </Layout>
  );
};

const ExpiringCerts = ({ rows, loading }) => (
  <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
    <div className="border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-800">
      Certifications expiring in 30 days
    </div>
    {loading ? (
      <div className="p-4">
        <Loader />
      </div>
    ) : rows.length === 0 ? (
      <p className="px-4 py-6 text-sm text-gray-500">None due.</p>
    ) : (
      <ul className="divide-y divide-gray-100 text-sm">
        {rows.map((c) => (
          <li key={c.id} className="flex justify-between px-4 py-2">
            <span>
              {c.worker_name} · {String(c.kind || '').replaceAll('_', ' ')}
            </span>
            <span className={c.expired ? 'text-danger-fg' : 'text-warning-fg'}>
              {c.expired ? 'expired' : 'due'} {String(c.expires_on).slice(0, 10)}
            </span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const AddMinimumWageModal = ({ busy, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    state: '',
    zone: '',
    skill_level: '',
    trade: '',
    minimum_per_day: '',
    effective_from: new Date().toISOString().slice(0, 10),
    notification_reference: ''
  });
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Modal name="Add a notified minimum wage" onClose={onClose} width="520px">
      <div className="space-y-3">
        <p className="rounded bg-gray-50 px-3 py-2 text-xs text-gray-600">
          Leave skill and trade blank for a figure that applies to everybody. A
          row naming both is used in preference to a blanket one.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Skill level">
            <select
              value={form.skill_level}
              onChange={set('skill_level')}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">All</option>
              {SKILLS.map((s) => (
                <option key={s} value={s}>
                  {titleCase(s)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Trade (optional)">
            <input
              value={form.trade}
              onChange={set('trade')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="State">
            <input
              value={form.state}
              onChange={set('state')}
              placeholder="Karnataka"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Zone (optional)">
            <input
              value={form.zone}
              onChange={set('zone')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Minimum per day">
            <input
              type="number"
              value={form.minimum_per_day}
              onChange={set('minimum_per_day')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Effective from">
            <input
              type="date"
              value={form.effective_from}
              onChange={set('effective_from')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </Field>
        </div>

        <Field label="Notification reference">
          <input
            value={form.notification_reference}
            onChange={set('notification_reference')}
            placeholder="KA/LAB/2026/17"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">
            So somebody can check the figure against the gazette later.
          </p>
        </Field>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              const amount = Number(form.minimum_per_day);
              if (!Number.isFinite(amount) || amount <= 0) {
                toast.error('Enter a positive minimum per day');
                return;
              }
              onSubmit({
                ...form,
                minimum_per_day: amount,
                skill_level: form.skill_level || null,
                trade: form.trade || null,
                state: form.state || null,
                zone: form.zone || null,
                notification_reference: form.notification_reference || null
              });
            }}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {busy ? 'Saving…' : 'Record'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const Verdict = ({ compliant }) => {
  if (compliant === false) {
    return (
      <span className="rounded bg-danger-bg px-2 py-0.5 text-xs font-medium text-danger-fg">
        Below minimum
      </span>
    );
  }
  if (compliant === true) {
    return (
      <span className="rounded bg-success-bg px-2 py-0.5 text-xs font-medium text-success-fg">
        OK
      </span>
    );
  }
  // Amber, not green. Nothing was checked.
  return (
    <span className="rounded bg-warning-bg px-2 py-0.5 text-xs font-medium text-warning-fg">
      No minimum set
    </span>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div className="mb-1 text-xs font-medium text-gray-600">{label}</div>
    {children}
  </div>
);

const Th = ({ children, className = '' }) => (
  <th className={`whitespace-nowrap px-4 py-3 text-left font-medium ${className}`}>
    {children}
  </th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
);

function money(value) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '₹0';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

function shortDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}

function titleCase(value) {
  if (!value) return '';
  return value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default WageCompliance;
